import { useMemo } from "react";

import PersonalVideoIcon from "@mui/icons-material/PersonalVideo";
import TvOffIcon from "@mui/icons-material/TvOff";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid2";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";

import DoitLogo from "../../../assets/DoitLogo.svg";
import { useTvMode } from "../../../Context/TvModeContext";
import { CloudConnect } from "../CloudConnect";
import { KeyContactsSection } from "../KeyContactsSection/KeyContactsSection";
import QuickLinks from "../QuickLinks";
import { useAmountInCustomerCurrency } from "./AmountInCustomerCurrency";
import AcknowledgedAnomaliesList from "./CardLists/AcknowledgedAnomaliesList";
import { ActiveAnomaliesList } from "./CardLists/ActiveAnomaliesList";
import ActiveBudgetsList from "./CardLists/ActiveBudgetsList";
import { ActiveInsightsList } from "./CardLists/ActiveInsightsList";
import { CloudWasteCard } from "./Cards/CloudWasteCard";
import { InsightsCard } from "./Cards/InsightsCard";
import { RecentInquiriesCard } from "./Cards/RecentInquiriesCard";
import { CloudCostsSection } from "./CloudCostsSection";
import { useActiveBudgets, useActiveCommitments, useAnomalies, useInsightsPotentialSavings } from "./hooks";

const NewHome = () => {
  const { tvMode, toggleTvMode } = useTvMode();

  // const handlePrint = () => {
  //   const specific_element = document.getElementById("print-content")?.innerHTML;
  //   const original_elements = document.body.innerHTML;
  //   if (specific_element) {
  //     document.body.innerHTML = specific_element;
  //     window.print();
  //   }
  //   if (original_elements) {
  //     document.body.innerHTML = original_elements;
  //   }
  // };

  const {
    totalCostAtRisk: anomaliesAtRisk,
    loading: anomaliesLoading,
    activeAnomalies,
    allAnomalies,
    numberOfAnomaliesInLast30Days,
    anomaliesCostAtRiskInLast7Days,
  } = useAnomalies();

  const {
    totalCostAtRisk: budgetsAtRisk,
    loading: budgetsLoading,
    budgets,
    budgetsNearThreshold,
    budgetsCostAtRiskInLast7Days,
  } = useActiveBudgets();
  const {
    totalPotentialSavings: insightsRisk,
    isLoading: insightsLoading,
    cloudFlowsCount,
    cloudFlowsRisk,
    insightsCostAtRiskInLast7Days,
    allInsights,
  } = useInsightsPotentialSavings();
  const { commitmentsCostAtRisk, loading: commitmentsLoading, activeCommitmentsAtRiskCount } = useActiveCommitments();

  const acknowledgedAnomalies = useMemo(
    () => allAnomalies.filter((anomaly) => !!anomaly.customerFeedback),
    [allAnomalies]
  );

  const budgetsAtRiskInCustomerCurrency = useAmountInCustomerCurrency(budgetsCostAtRiskInLast7Days);

  const costAtRiskInLast7Days =
    budgetsAtRiskInCustomerCurrency + anomaliesCostAtRiskInLast7Days + insightsCostAtRiskInLast7Days;

  return (
    <Box>
      {tvMode && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: -3 }}>
          <img src={DoitLogo} alt="DoiT Logo" style={{ width: 78, height: 24 }} />
        </Box>
      )}

      <Stack direction="row" alignItems="center" justifyContent="flex-end">
        {/* <Tooltip title="Print" arrow>
              <IconButton data-cy="print-btn" sx={{ mr: 0.5 }} onClick={handlePrint}>
                <PrintIcon />
              </IconButton>
            </Tooltip> */}
        <Tooltip title={tvMode ? "Exit TV mode" : "Enter TV mode"} arrow>
          <IconButton
            data-cy="tv-mode-btn"
            onClick={(e) => {
              toggleTvMode();
              e.currentTarget.blur();
            }}
          >
            {tvMode ? <TvOffIcon /> : <PersonalVideoIcon />}
          </IconButton>
        </Tooltip>
      </Stack>

      <Grid container spacing={2} mb={3} id="print-content">
        <Grid size={{ xs: 12, lg: 3 }}>
          <CloudWasteCard
            anomaliesAtRisk={anomaliesAtRisk}
            budgetsAtRisk={budgetsAtRisk}
            insightsRisk={insightsRisk}
            commitmentsAtRisk={commitmentsCostAtRisk}
            anomaliesLoading={anomaliesLoading}
            budgetsLoading={budgetsLoading}
            insightsLoading={insightsLoading}
            commitmentsLoading={commitmentsLoading}
            costAtRiskInLast7Days={costAtRiskInLast7Days}
          />
        </Grid>
        <Grid size={{ xs: 12, lg: 3 }}>
          <InsightsCard insightsRisk={insightsRisk} loading={insightsLoading} />
        </Grid>
        <Grid size={{ xs: 12, lg: 6 }}>
          <RecentInquiriesCard />
        </Grid>
        <CloudCostsSection
          cloudFlowsCount={cloudFlowsCount}
          cloudFlowsRisk={cloudFlowsRisk}
          activeCommitmentsAtRiskCount={activeCommitmentsAtRiskCount}
          commitmentsCostAtRisk={commitmentsCostAtRisk}
          loading={commitmentsLoading}
          anomaliesAtRisk={anomaliesAtRisk}
          numberOfAnomaliesInLast30Days={numberOfAnomaliesInLast30Days ?? 0}
          numberOfAnomalies={activeAnomalies?.length ?? 0}
          numberOfBudgets={budgets?.length ?? 0}
          numberOfBudgetsNearThreshold={budgetsNearThreshold.length ?? 0}
          budgetsAtRisk={budgetsAtRisk}
        />

        <Grid size={{ xs: 12, md: 6 }} mt={2}>
          <ActiveAnomaliesList anomalies={allAnomalies ?? []} />
        </Grid>

        {acknowledgedAnomalies.length > 0 && (
          <Grid size={{ xs: 12, md: 6 }} mt={2}>
            <AcknowledgedAnomaliesList acknowledgedAnomalies={acknowledgedAnomalies} />
          </Grid>
        )}

        {(insightsLoading || allInsights.length > 0) && (
          <Grid size={{ xs: 12, md: 6 }} sx={{ mt: 2 }}>
            <ActiveInsightsList insights={allInsights} costAtRisk={insightsRisk} loading={insightsLoading} />
          </Grid>
        )}

        <Grid size={{ xs: 12, md: 6 }} sx={{ mt: 2 }}>
          <ActiveBudgetsList budgets={budgets} budgetsAtRisk={budgetsAtRisk} />
        </Grid>
      </Grid>

      <CloudConnect />

      {/* Key contacts section */}
      {!tvMode && (
        <Grid container spacing={2} sx={{ mt: 3 }}>
          <Grid size={{ xs: 12 }}>
            <QuickLinks />
          </Grid>
          <Grid size={{ xs: 12 }} sx={{ mt: 1 }}>
            <KeyContactsSection />
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default NewHome;
